import React, { Fragment, useEffect, useState } from "react";
import { FormCanvas } from "components/form-fields";
import { ShowHide } from "components/layout";
import { useQuery } from "hooks/useQuery";
import { ButtonSave } from "components/buttons";
import { EmployeeService } from "services";
import {
    NotificationToaster,
    SuccessPanel,
    FailurePanel,
} from "components/notifications";
import { Intent } from "@blueprintjs/core";
import { DateToLocal } from "components/dates";
import { GridLayout } from "components/grid";
import { UnauthenticatedPage } from "pages/layouts";

export function DebriefSignOff() {
    const debriefSignedOffCode = 400;
    const query = useQuery();
    const code = query.get("code");
    const debrief = query.get("debrief");
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [showAlreadySigned, setShowAlreadySigned] = useState(false);
    const [showError, setShowError] = useState(false);
    const [debriefName, setDebriefName] = useState("");
    const [debriefDate, setDebriefDate] = useState(null);
    const [signature, setSignature] = useState("");

    useEffect(() => {
        if (!code || !debrief) {
            return;
        }
        EmployeeService.getDebriefSignOff(debrief, code)
            .then(
                (result) => {
                    setDebriefName(result.debrieferName);
                    setDebriefDate(result.debriefDate);
                },
                (error) => {
                    if (error.response.status === debriefSignedOffCode) {
                        setShowAlreadySigned(true);
                    } else {
                        setShowError(true);
                    }
                }
            )
            .catch(() => {
                NotificationToaster.show(
                    Intent.DANGER,
                    "Could not get debrief id, please try again."
                );
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, []);

    function handleSave() {
        setIsSaving(true);

        EmployeeService.saveDebriefSignOff(debrief, code, signature)
            .then(
                () => {
                    setShowSuccess(true);
                },
                () => {
                    NotificationToaster.show(
                        Intent.DANGER,
                        "Could not get debrief id, please try again."
                    );
                }
            )
            .catch(() => {
                NotificationToaster.show(
                    Intent.DANGER,
                    "Could not get debrief id, please try again."
                );
            })
            .finally(() => {
                setIsSaving(false);
            });
    }

    return (
        <UnauthenticatedPage>
            <>
                <ShowHide
                    evaluator={showSuccess}
                    show={
                        <Fragment>
                            <h2>Driver Debrief Signed Off</h2>
                            <SuccessPanel>
                                <p>
                                    This driver debrief has been successfully
                                    signed off.
                                </p>
                            </SuccessPanel>
                        </Fragment>
                    }
                />
                <ShowHide
                    evaluator={showAlreadySigned}
                    show={
                        <Fragment>
                            <h2>Already Complete</h2>
                            <SuccessPanel>
                                <p>
                                    This driver debrief has already been signed
                                    off.
                                </p>
                                <p>
                                    If you did not sign this debrief off, please
                                    contact your manager/Vision administrator
                                    immediately.
                                </p>
                            </SuccessPanel>
                        </Fragment>
                    }
                />
                <ShowHide
                    evaluator={showError}
                    show={
                        <Fragment>
                            <h2>This debrief does not exist</h2>
                            <FailurePanel>
                                <p>
                                    No record can be found for this driver
                                    debrief.
                                </p>
                                <p>
                                    Please get in touch with your manager/Vision
                                    administrator to clarify.
                                </p>
                            </FailurePanel>
                        </Fragment>
                    }
                />
                <ShowHide
                    evaluator={
                        !showSuccess &&
                        !showAlreadySigned &&
                        !showError
                    }
                    show={
                        <Fragment>
                            <h2>Driver Debrief Sign Off</h2>
                            <p>
                                The following debrief will be marked as
                                electronically signed and recorded in your
                                driver profile - debrief archive.
                            </p>
                            <GridLayout numberOfColumns={4} gap={20}>
                                <div className="spacer-bottom">
                                    <h4 className="bold">Debriefer Name</h4>
                                    <div>{debriefName}</div>
                                </div>
                                <div>
                                    <h4 className="bold">Debrief Date</h4>
                                    <DateToLocal format="DD/MM/YY">
                                        {debriefDate}
                                    </DateToLocal>
                                </div>
                            </GridLayout>
                            <FormCanvas
                                isLoading={isLoading}
                                penColour="grey"
                                headingText="Signature"
                                helperText="Please sign in the box below by either drawing with your cursor on desktop or tapping on mobile"
                                onChange={(value) =>
                                    setSignature(value.split(",").pop())
                                }
                                showDate
                            />
                            <ButtonSave
                                onClick={handleSave}
                                loading={isLoading}
                                simpleDisabled={isSaving || !signature}
                            />
                        </Fragment>
                    }
                />
            </>
        </UnauthenticatedPage>
    );
}
